import React, { useEffect, useState, useCallback, useContext } from "react";
import { Grid, Button, Typography, Box } from "@mui/material";

import SearchFilter from "./SearchFilter";
import SortByFilter from "./SortFilter";
import StatusFilter from "./StatusFilter";
import AssigneeFilter from "./AssigneeFilter";
import PlatformFilter from "./PlatformFilter";
import DateFilter from "./DateFilter";
import CenterFilter from "./CenterFilter";
import TestResult from "./TestResult";
import InterpretationStatusFilter from "./InterpretationStatusFilter";
import TypeLabelFilter from "./TypeFilter";
import RolesFilter from "./RolesFilter";
import DoctorFilter from "./DoctorFilters";
import SpokeFilter from "./AllSpoke";

import { AuthContext } from "../../context/AuthContextProvider";
import CloseIcon from "@mui/icons-material/Close";

// safer initial state creator
const getInitialFilters = () => ({
  searchText: "",
  sort: "desc",
  status: [],
  assignees: [],
  platform: [],
  testResult: [],
  dateFrom: null,
  dateTo: null,
  hubIds: [],
  spokeIds: [],
  type: [],
  doctorIds: [],
  roles: [],
});

function CustomFilters({ onApply, component, rightContent }) {
  const { user } = useContext(AuthContext);
  const FILTER_STORAGE_KEY = `filters:${component}`;

  const [filters, setFilters] = useState(() => {
    const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return saved
      ? { ...getInitialFilters(), ...JSON.parse(saved) }
      : getInitialFilters();
  });

  //  update single filter
  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  //  persist filters
  useEffect(() => {
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  }, [filters, FILTER_STORAGE_KEY]);

  //  auto apply filters
  useEffect(() => {
    onApply(filters);
  }, [filters]);

  //  CLEAR FILTERS FUNCTION
  const handleClearFilters = () => {
    const reset = getInitialFilters();
    setFilters(reset);
    sessionStorage.removeItem(FILTER_STORAGE_KEY);
  };

  //  optional: disable button if already default
  const isDefault =
    JSON.stringify(filters) === JSON.stringify(getInitialFilters());

  return (
    <>
      <Grid
        container
        alignItems="center"
        sx={{
          mt: 2,
          gap: { xs: 1, sm: 1.5, md: "12px" },
          flexWrap: "nowrap",
          overflowX: { xs: "auto", md: "auto" },
          whiteSpace: "nowrap",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {/* SORT */}
        <Grid item sx={{ flex: "0 0 auto" }}>
          <SortByFilter
            value={filters.sort}
            onChange={(v) => updateFilter("sort", v)}
            component={component}
          />
        </Grid>

        {/* TYPE */}
        {component === "centres" && (
          <Grid item sx={{ flex: "0 0 auto" }}>
            <TypeLabelFilter
              value={filters.type}
              onChange={(v) => updateFilter("type", v)}
              component={component}
            />
          </Grid>
        )}

        {/* CENTER / SPOKE */}
        {(component !== "referred-cases" && user.userType === "b2b-admin") ||
          user.userType === "b2b-doctor" ||
          user.userType === "b2b-member" ||
          user.userType === "b2b" ? (
          <Grid item sx={{ flex: "0 0 auto" }}>
            <CenterFilter
              hubIds={filters.hubIds}
              spokeIds={filters.spokeIds}
              onChange={({ hubIds, spokeIds }) => {
                setFilters((prev) => ({
                  ...prev,
                  hubIds,
                  spokeIds,
                }));
              }}
              component={component}
            />
          </Grid>
        ) : (
          <Grid item sx={{ flex: "0 0 auto" }}>
            <SpokeFilter
              hubIds={filters.hubIds}
              spokeIds={filters.spokeIds}
              onChange={({ hubIds, spokeIds }) => {
                setFilters((prev) => ({
                  ...prev,
                  hubIds,
                  spokeIds,
                }));
              }}
              component={component}
            />
          </Grid>
        )}

        {/* DATE */}
        {component !== "centres" && component !== "members" && (
          <Grid item sx={{ flex: "0 0 auto" }}>
            <DateFilter
              value={{ from: filters.dateFrom, to: filters.dateTo }}
              onChange={({ dateFrom, dateTo }) => {
                setFilters((prev) => ({
                  ...prev,
                  dateFrom,
                  dateTo,
                }));
              }}
              component={component}
            />
          </Grid>
        )}

        {/* ROLES */}
        {component === "members" && (
          <Grid item sx={{ flex: "0 0 auto" }}>
            <RolesFilter
              value={filters.roles}
              onChange={(v) => updateFilter("roles", v)}
              component={component}
            />
          </Grid>
        )}

        {/* STATUS */}
        {component !== "ecg" &&
          component !== "interpretations" &&
          component !== "referred-cases" &&
          component !== "centres" &&
          component !== "members" ? (
          <Grid item sx={{ flex: "0 0 auto" }}>
            <StatusFilter
              value={filters.status}
              onChange={(v) => updateFilter("status", v)}
              component={component}
            />
          </Grid>
        ) : null}

        {/* ASSIGNEE / INTERPRETATION */}
        {component !== "ecg" &&
          component !== "interpretations" &&
          component !== "referred-cases" &&
          component !== "centres" &&
          component !== "members" ? (
          <Grid item sx={{ flex: "0 0 auto" }}>
            <AssigneeFilter
              value={filters.assignees}
              onChange={(v) => updateFilter("assignees", v)}
            />
          </Grid>
        ) : (
          component !== "interpretations" &&
          component !== "referred-cases" &&
          component !== "centres" &&
          component !== "members" && (
            <Grid item sx={{ flex: "0 0 auto" }}>
              <InterpretationStatusFilter
                value={filters.status}
                onChange={(v) => updateFilter("status", v)}
                component={component}
              />
            </Grid>
          )
        )}

        {/* PLATFORM / TEST RESULT */}
        {component !== "ecg" &&
          component !== "interpretations" &&
          component !== "referred-cases" &&
          component !== "centres" &&
          component !== "members" ? (
          <Grid item sx={{ flex: "0 0 auto" }}>
            <PlatformFilter
              value={filters.platform}
              onChange={(v) => updateFilter("platform", v)}
              component={component}
            />
          </Grid>
        ) : (
          component !== "interpretations" &&
          component !== "referred-cases" &&
          component !== "centres" &&
          component !== "members" && (
            <Grid item sx={{ flex: "0 0 auto" }}>
              <TestResult
                value={filters.testResult}
                onChange={(v) => updateFilter("testResult", v)}
                component={component}
              />
            </Grid>
          )
        )}

        {/* DOCTOR */}
        {component === "referred-cases" &&
          (user.userType === "b2b" || user.userType === "b2b-admin") && (
            <Grid item sx={{ flex: "0 0 auto" }}>
              <DoctorFilter
                value={filters.doctorIds}
                onChange={(v) => updateFilter("doctorIds", v)}
                component={component}
              />
            </Grid>
          )}

        {rightContent && (
          <Grid item sx={{ ml: "auto", flex: "0 0 auto" }}>
            {rightContent}
          </Grid>
        )}

        {/*  CLEAR FILTER BUTTON */}
        {!isDefault && (
          <Grid item sx={{ ml: rightContent ? 0 : "auto", flex: "0 0 auto" }}>
            <Box
              onClick={handleClearFilters}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "3px",
                cursor: "pointer",
                color: "#5751E9",
                fontWeight: 600,
                fontSize: "16px",
                textDecoration: "underline",
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "15px",
                  textDecoration: "underline",
                }}
              >
                Clear Filters
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </>
  );
}

export default CustomFilters;
